import React, { useState, useEffect } from 'react';
import {
  checkTallyConnection,
  TallyConnectionStatus,
} from '../../lib/tally/tallyExport';

interface TallyConnectorProps {
  onConnectionStatusChange: (status: TallyConnectionStatus) => void;
}

const TallyConnector: React.FC<TallyConnectorProps> = ({
  onConnectionStatusChange,
}) => {
  const [status, setStatus] = useState<TallyConnectionStatus>({
    isConnected: false,
    message: 'Checking connection...',
  });
  const [checking, setChecking] = useState<boolean>(false);

  useEffect(() => {
    checkConnection();
  }, []);

  const checkConnection = async () => {
    setChecking(true);
    try {
      const connectionStatus = await checkTallyConnection();
      setStatus(connectionStatus);
      onConnectionStatusChange(connectionStatus);
    } catch (error) {
      setStatus({
        isConnected: false,
        message: 'Failed to connect to Tally service',
      });
      onConnectionStatusChange({
        isConnected: false,
        message: 'Failed to connect to Tally service',
      });
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="mb-6 p-4 bg-gray-50 rounded-lg border">
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <div
            className={`w-3 h-3 rounded-full mr-2 ${status.isConnected ? 'bg-green-500' : 'bg-red-500'}`}></div>
          <span className="font-medium">
            Tally Connection:{' '}
            {status.isConnected ? 'Connected' : 'Disconnected'}
          </span>
        </div>

        <button
          onClick={checkConnection}
          disabled={checking}
          className="px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-blue-300 transition">
          {checking ? 'Checking...' : 'Refresh'}
        </button>
      </div>

      <p className="mt-2 text-sm text-gray-600">{status.message}</p>

      {!status.isConnected && (
        <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-md text-sm">
          <p className="font-medium text-yellow-800">Troubleshooting steps:</p>
          <ol className="mt-1 list-decimal list-inside text-yellow-700">
            <li>Ensure Tally Prime is running</li>
            <li>Verify TallyPrime Edit Log (EL) is active</li>
            <li>Check that Tally is listening on port 9000</li>
            <li>Make sure your firewall is not blocking the connection</li>
          </ol>
        </div>
      )}
    </div>
  );
};

export default TallyConnector;
